@if (isLoading || hasData) {
  <div class="nbt-wrapper">
    <h2 class="nbt-title">Next Best Trade Recommendations</h2>
    <p class="nbt-subtitle">
      Gap Closed = share of the Sharpe gap from the Baseline Portfolio to the
      maximum-Sharpe portfolio on the efficient frontier that this step covers.
    </p>
    @if (isLoading) {
      <div class="nbt-loading">
        <mat-spinner diameter="32"></mat-spinner>
      </div>
    }
    @if (!isLoading && hasData) {
      <div>
        <table class="table nbt-table">
          <thead>
            <tr>
              <th>Portfolio</th>
              <th class="text-end">Trades</th>
              <th class="text-end">Exp. Return</th>
              <th class="text-end">Risk</th>
              <th class="text-end">Sharpe</th>
              <th class="text-end">Sharpe &Delta;</th>
              <th class="text-end">Gap Closed</th>
              <th class="text-end">Turnover</th>
            </tr>
          </thead>
          <tbody>
            @for (row of tableRows; track row.key) {
              <tr
                [class.nbt-row-clickable]="
                  !row.isBaseline && !row.isSelectedOptimal && !row.isEmpty
                "
                [class.nbt-row-expanded]="expandedStep === row.key"
                [class.nbt-row-baseline]="row.isBaseline"
                [class.nbt-row-selected-optimal]="row.isSelectedOptimal"
                [class.nbt-row-empty]="row.isEmpty"
                [matTooltip]="row.emptyTooltip ?? ''"
                [matTooltipDisabled]="!row.isEmpty"
                matTooltipPosition="above"
                (click)="
                  !row.isBaseline &&
                    !row.isSelectedOptimal &&
                    !row.isEmpty &&
                    toggleRow(row.key)
                "
              >
                <td>
                  <span class="nbt-portfolio-label">{{ row.portfolio }}</span>
                </td>
                <td class="text-end">{{ row.trades }}</td>
                @if (row.isEmpty) {
                  <td colspan="6" class="nbt-empty-message">
                    {{ row.emptyShortLabel }}
                  </td>
                } @else {
                  <td class="text-end">{{ formatPercent(row.expReturn) }}</td>
                  <td class="text-end">{{ formatPercent(row.risk) }}</td>
                  <td class="text-end">{{ formatSharpe(row.sharpe) }}</td>
                  <td class="text-end">
                    @if (row.sharpeDelta != null && !row.isBaseline) {
                      <span [class.text-success]="row.sharpeDelta > 0">
                        {{ row.sharpeDelta >= 0 ? "+" : ""
                        }}{{ formatSharpe(row.sharpeDelta) }}
                      </span>
                    }
                    @if (row.isBaseline) {
                      <span>{{ formatSharpe(0) }}</span>
                    }
                  </td>
                  <td class="text-end">
                    {{
                      row.gapClosed != null ? formatPercent(row.gapClosed) : "-"
                    }}
                  </td>
                  <td class="text-end">
                    {{ row.turnover != null ? formatPercent(row.turnover) : "-" }}
                  </td>
                }
              </tr>
              <!-- Expanded detail row -->
              @if (expandedStep === row.key && row.step) {
                <tr class="nbt-detail-row">
                  <td colspan="8">
                    <div class="nbt-detail">
                      <!-- Trade description -->
                      <p class="nbt-detail-desc">{{ row.step.description }}</p>
                      <!-- Metrics comparison -->
                      <h4 class="nbt-detail-heading">Metrics Comparison</h4>
                      <table class="table nbt-metrics-table">
                        <thead>
                          <tr>
                            <th>Metric</th>
                            <th class="text-end">Baseline</th>
                            <th class="text-end">{{ row.portfolio }}</th>
                            <th class="text-end">Difference</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td>Exp. Return</td>
                            <td class="text-end">
                              {{
                                formatPercent(
                                  baselineMetrics
                                    ? baselineMetrics.expReturn / 100
                                    : null
                                )
                              }}
                            </td>
                            <td class="text-end">
                              {{ formatPercent(row.step.exp_return) }}
                            </td>
                            <td class="text-end">
                              @if (baselineMetrics) {
                                <span>
                                  {{
                                    formatPercent(
                                      row.step.exp_return -
                                        baselineMetrics.expReturn / 100
                                    )
                                  }}
                                </span>
                              }
                            </td>
                          </tr>
                          <tr>
                            <td>Risk</td>
                            <td class="text-end">
                              {{
                                formatPercent(
                                  baselineMetrics
                                    ? baselineMetrics.risk / 100
                                    : null
                                )
                              }}
                            </td>
                            <td class="text-end">
                              {{ formatPercent(row.step.risk) }}
                            </td>
                            <td class="text-end">
                              @if (baselineMetrics) {
                                <span>
                                  {{
                                    formatPercent(
                                      row.step.risk - baselineMetrics.risk / 100
                                    )
                                  }}
                                </span>
                              }
                            </td>
                          </tr>
                          <tr>
                            <td>Sharpe</td>
                            <td class="text-end">
                              {{ formatSharpe(forwardBaselineSharpe) }}
                            </td>
                            <td class="text-end">
                              {{ formatSharpe(row.step.sharpe) }}
                            </td>
                            <td class="text-end">
                              @if (forwardBaselineSharpe != null) {
                                <span
                                  [class.text-success]="
                                    getSharpeImprovement() > 0
                                  "
                                  [class.text-danger]="
                                    getSharpeImprovement() < 0
                                  "
                                >
                                  {{ getSharpeImprovement() >= 0 ? "+" : ""
                                  }}{{ formatSharpe(getSharpeImprovement()) }}
                                </span>
                              }
                            </td>
                          </tr>
                        </tbody>
                      </table>
                      <!-- Before/After Weights -->
                      <h4 class="nbt-detail-heading">Weight Changes</h4>
                      @if (weightChanges.length) {
                        <table class="table nbt-weights-table">
                          <thead>
                            <tr>
                              <th>Asset</th>
                              <th class="text-end">Before</th>
                              <th class="text-end">After</th>
                              <th class="text-end">Change</th>
                            </tr>
                          </thead>
                          <tbody>
                            @for (wc of weightChanges; track wc.asset) {
                              <tr [class.nbt-unchanged-row]="wc.change === 0">
                                <td>{{ wc.asset }}</td>
                                <td class="text-end">
                                  {{
                                    wc.change !== 0
                                      ? formatPercent(wc.before)
                                      : ""
                                  }}
                                </td>
                                <td class="text-end">
                                  {{
                                    wc.change !== 0
                                      ? formatPercent(wc.after)
                                      : ""
                                  }}
                                </td>
                                <td class="text-end">
                                  @if (wc.change !== 0) {
                                    <span
                                      [class.text-success]="wc.change > 0"
                                      [class.text-danger]="wc.change < 0"
                                    >
                                      {{ wc.change > 0 ? "+" : ""
                                      }}{{ formatPercent(wc.change) }}
                                    </span>
                                  }
                                </td>
                              </tr>
                            }
                          </tbody>
                        </table>
                      }
                      <!-- Apply Trade button -->
                      <div class="nbt-apply-wrapper">
                        <button
                          mat-raised-button
                          color="primary"
                          [disabled]="isSaving"
                          (click)="
                            applyTrade(row.step); $event.stopPropagation()
                          "
                        >
                          @if (isSaving) {
                            <mat-spinner
                              diameter="18"
                              class="me-2"
                            ></mat-spinner>
                          }
                          Save as selected portfolio
                        </button>
                      </div>
                    </div>
                  </td>
                </tr>
              }
            }
          </tbody>
        </table>
      </div>
    }
  </div>
}
